import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import localeEn from '@angular/common/locales/en';

import { AppRoutingModule } from './app-routing.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import { ScrollingModule } from '@angular/cdk/scrolling';

import { CustomDateAdapter } from './utils/custom-date-adapter';

// PIPES
import { LocalePipe } from './locale.pipe';
import { LocalizedNamePipe } from './localized-name.pipe';
import { IsRequiredDirective } from './shared/directives/is-required.directive';
import { ErrorMessagePipe } from './shared/pipes/error-message.pipe';
import { FillArrayPipe } from './shared/pipes/fill-array.pipe';
import { MomentDatePipe } from './shared/pipes/moment-date.pipe';
import { FileSizePipe } from './shared/pipes/file-size.pipe';
import { FileSizeDecimalPipe } from './shared/pipes/file-size-decimal.pipe';

//COMPONENTS
import { DoctorComponent } from './call-form/doctor/doctor.component';
import { DiagnosisComponent } from './call-form/diagnosis/diagnosis.component';
import { PrescriptionComponent } from './call-form/prescription/prescription.component';
import { MedicalOrderComponent } from './call-form/medical-order/medical-order.component';
import { RateComponent } from './call-form/rate/rate.component';
import { PharmacyPickupComponent } from './call-form/rate/pharmacy-pickup/pharmacy-pickup.component';
import { AppComponent } from './app.component';
import { LoaderInterceptor } from './loader.interceptor';
import { ErrorInterceptor } from './error.interceptor';
import { MonitoringComponent } from './call-form/monitoring/monitoring.component';
import { MessageComponent } from './message/message.component';
import { DialogComponent } from './shared/components/dialog/dialog.component';
import { DocumentationComponent } from './documentation/documentation.component';
import { EnrollmentComponent } from './documentation/components/enrollment/enrollment.component';
import { EnrollmentFormComponent } from './documentation/components/enrollment-form/enrollment-form.component';
import { EnrollmentDialogComponent } from './documentation/components/enrollment-dialog/enrollment-dialog.component';
import { FileInputComponent } from './shared/file-input/file-input.component';
import { SelectComponent } from './shared/components/select/select.component';
import { DatepickerComponent } from './shared/components/datepicker/datepicker.component';
import { ToastComponent } from './shared/components/toast/toast.component';
import { ToolbarComponent } from './shared/components/toolbar/toolbar.component';
import { AppointmentCancelComponent } from './appointment/appointment-cancel/appointment-cancel.component';
import { AutocompleteComponent } from './shared/components/autocomplete/autocomplete.component';
import { CallInformationComponent } from './call-form/call-information/call-information.component';
import { DefaultTemplateComponent } from './call-form/default-template/default-template.component';
import { MedicineFormComponent } from './call-form/medicine-form/medicine-form.component';
import { ItemComponent } from './shared/components/item/item.component';
import { MedicalOrderFormComponent } from './call-form/medical-order-form/medical-order-form.component';
import { MedicalRecordsComponent } from './call-form/medical-records/medical-records.component';
import { MedicalRecordComponent } from './call-form/medical-record/medical-record.component';
import { MedicalExamsComponent } from './call-form/medical-exams/medical-exams.component';
import { SendMedicalRecordsComponent } from './medical-records/send-medical-records/send-medical-records.component';
import { UploadExternalPrescriptionDialogComponent } from './call-form/upload-external-prescription-dialog/upload-external-prescription-dialog.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { LogoComponent } from './shared/components/logo/logo.component';
import { AddressFormComponent } from './shared/components/address-form/address-form.component';
import { MapComponent } from './shared/components/map/map.component';
import { ConfirmPrescriptionDialogComponent } from './call-form/confirm-prescription-dialog/confirm-prescription-dialog.component';

//SERVICES
import { LocaleService } from './services/locale.service';

//MATERIAL
import { MatStepperModule } from '@angular/material/stepper';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule, MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MatDateFormats, MatNativeDateModule, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialogModule, MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { MatTableModule } from '@angular/material/table';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { RatingCallComponent } from './rate-call/rating-call/rating-call.component';
import { DoctorCardComponent } from './rate-call/doctor-card/doctor-card.component';
import { ScoreComponent } from './rate-call/score/score.component';
import { MatListModule } from '@angular/material/list';
import { MatChipsModule } from '@angular/material/chips';

registerLocaleData(localeEs);
registerLocaleData(localeEn);

export function setupTranslateFactory(localeService: LocaleService) {
  const locale = window.navigator.language;
  return () => localeService.use(locale.toLowerCase());
}

const CUSTOM_DATE_FORMAT: MatDateFormats = {
  parse: {
    dateInput: 'DD/MM/YYYY' // this is how the date will be parsed from input
  },
  display: {
    dateInput: 'DD/MM/YYYY', // this is how the date will get displayed on the input
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

@NgModule({
  declarations: [
    AppComponent,
    DoctorComponent,
    DiagnosisComponent,
    PrescriptionComponent,
    MedicalOrderComponent,
    RateComponent,
    PharmacyPickupComponent,
    LocalePipe,
    MonitoringComponent,
    MessageComponent,
    DialogComponent,
    LocalizedNamePipe,
    DocumentationComponent,
    EnrollmentComponent,
    EnrollmentFormComponent,
    EnrollmentDialogComponent,
    FileInputComponent,
    AppointmentCancelComponent,
    SelectComponent,
    IsRequiredDirective,
    ErrorMessagePipe,
    DatepickerComponent,
    ToastComponent,
    ToolbarComponent,
    AppointmentCancelComponent,
    AutocompleteComponent,
    CallInformationComponent,
    DefaultTemplateComponent,
    MedicineFormComponent,
    ItemComponent,
    MedicalOrderFormComponent,
    FillArrayPipe,
    MedicalRecordsComponent,
    MedicalRecordComponent,
    MomentDatePipe,
    MedicalExamsComponent,
    FileSizePipe,
    SendMedicalRecordsComponent,
    UploadExternalPrescriptionDialogComponent,
    FileSizeDecimalPipe,
    RatingCallComponent,
    DoctorCardComponent,
    FooterComponent,
    LogoComponent,
    AddressFormComponent,
    MapComponent,
    ConfirmPrescriptionDialogComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatStepperModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatGridListModule,
    MatButtonModule,
    MatTabsModule,
    MatCardModule,
    MatExpansionModule,
    MatDatepickerModule,
    MatNativeDateModule,
    NgxMatTimepickerModule,
    MatDialogModule,
    MatRadioModule,
    MatTableModule,
    MatSnackBarModule,
    ScrollingModule,
    MatDividerModule,
    MatProgressBarModule,
    ScoreComponent,
    MatListModule,
    MatChipsModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: setupTranslateFactory,
      deps: [LocaleService],
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMAT },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: DateAdapter, useClass: CustomDateAdapter },
    LocalizedNamePipe,
    LocalePipe,
    FileSizeDecimalPipe,
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { closeOnNavigation: true, restoreFocus: false } },
    provideHttpClient(withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
